/// <reference types="@angular/localize" />

import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { PercentPipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideHighcharts } from 'highcharts-angular';

import { AppComponent } from './app/app.component';
import { appRoutes, routerConfig } from './app/app.routing';
import { environment } from './environments/environment';

import { AuthService } from './app/services/auth-service.service';
import { OutsideAuthService } from './app/services/outside-auth-service.service';
import { RightBridgeApiService } from './app/services/right-bridge-api.service';
import { TagsEvaluationService } from './app/services/tags-evaluation.service';
import { PermissionsService } from './app/services/permissions.service';
import { AppGlobalsService } from './app/services/app-globals.service';
import { SsoVerifyService } from './app/services/sso-verify.service';
import { AppAccessService } from './app/services/app-access.service';
import { TransferService } from './app/services/transfer.service';
import { ErrorReportingService } from './app/services/error-reporting.service';
import { ThemeService } from './app/services/theme.service';
import { EnvironmentService } from './app/services/environment.service';
import { UserPreferencesService } from './app/services/user-preferences.service';
import { CustomFiltersService } from './app/services/custom-filters.service';
import { NavHistoryService } from './app/services/nav-history.service';
import { CaseManagementService } from './app/case-management/case-management.service';
import { BookManagementService } from './app/book-management/book-management.service';
import { RolesRightsService } from './app/admin/roles-rights/roles-rights.service';
import { UserManagementService } from './app/admin/user-management/user-management.service';
import { TrainingService } from './app/training/training.service';
import { PostTradeService } from './app/post-trade/post-trade.service';
import { ReportingService } from './app/reporting/reporting.service';
import { SuggestionEngineService } from './app/suggestion-engine/suggestion-engine.service';
import { UnifiedFlowService } from './app/unified-flow/unified.service';
import {
  ErrorCatch,
  NoCacheHeadersInterceptor,
  VerifyImplementation,
} from './app/helpers/request-helpers.interceptor';
import { Factsheet } from './app/annuity-lookup/factsheet';
import { BeaconService } from './app/services/beacon.service';
import { BeaconServiceV2 } from './app/services/beaconv2.service';
import { RixtremaService } from './app/rixtrema/rixtrema.service';
import { FundFeeLookupService } from './app/fund-fee-lookup/fund-fee-lookup.service';
import { UniversalSearchService } from './app/universal-search/services/universal-search.service';
import {
  CdkDropList,
  CdkDrag,
  CdkDragPlaceholder,
  CdkDragPreview,
} from '@angular/cdk/drag-drop';
import { AdminService } from './app/admin/admin.service';
import { provideFormlyConfig } from './app/conditional-form/formly.config';
import { PostbackService } from './app/services/postback.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { provideNativeDateAdapter } from '@angular/material/core';
import { ngxLoadingAnimationTypes, NgxLoadingModule } from 'ngx-loading';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideFormlyConfig(),
    provideZoneChangeDetection(),
    provideAnimations(),
    provideRouter(
      appRoutes,
      withRouterConfig({
        onSameUrlNavigation: routerConfig.onSameUrlNavigation,
      }),
      withInMemoryScrolling({
        anchorScrolling: routerConfig.anchorScrolling,
        scrollPositionRestoration: 'top',
      })
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => {
      const initializerFn = ((environmentService: EnvironmentService) => {
        return () => {
          return environmentService.loadSettings();
        };
      })(inject(EnvironmentService));
      return initializerFn();
    }),
    RightBridgeApiService,
    CaseManagementService,
    BookManagementService,
    RolesRightsService,
    UserManagementService,
    TrainingService,
    PostTradeService,
    SuggestionEngineService,
    UnifiedFlowService,
    BeaconService,
    Factsheet,
    BeaconServiceV2,
    RixtremaService,
    FundFeeLookupService,
    UniversalSearchService,
    AdminService,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    CdkDragPreview,
    PostbackService,
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: {} },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'auto',
      },
    },
    AuthService,
    ThemeService,
    OutsideAuthService,
    TagsEvaluationService,
    PermissionsService,
    AppGlobalsService,
    SsoVerifyService,
    AppAccessService,
    TransferService,
    ReportingService,
    ErrorReportingService,
    UserPreferencesService,
    CustomFiltersService,
    NavHistoryService,
    PercentPipe,
    DecimalPipe,
    CurrencyPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheHeadersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorCatch,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: VerifyImplementation,
      multi: true,
    },
    { provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true } },
    { provide: 'googleTagManagerId', useValue: 'GTM-MDPDKCJ9' },
    provideHighcharts({
      instance: () =>
        import('highcharts/esm/highcharts').then(async m => {
          const Highcharts = m.default;

          await import('highcharts/esm/highcharts-more');
          await import('highcharts/esm/highcharts-3d');
          await import('highcharts/esm/modules/series-label');
          await import('highcharts/esm/modules/solid-gauge');
          await import('highcharts/esm/modules/accessibility');
          await import('highcharts/esm/modules/exporting');
          await import('highcharts/esm/modules/export-data');

          return Highcharts;
        }),
    }),
    provideNativeDateAdapter(),
    importProvidersFrom(
      NgxLoadingModule.forRoot({
        animationType: ngxLoadingAnimationTypes.circle,
        backdropBackgroundColour: 'rgba(0, 0, 0, 0.3)',
        fullScreenBackdrop: true,
        backdropBorderRadius: '0',
        primaryColour: 'rgb(38, 91, 151)',
        secondaryColour: 'rgba(38, 91, 151, 0.2)',
        tertiaryColour: '#265b97',
      })
    ),
  ],
});
